import { SelectOption } from '@globaleris/ui.library'
import { formatDateOnlyString } from '../../../../helpers/formatDateOnly'
import { DateFilters, DatePeriod } from '../../../../model'
import { AppConfigurationKey } from '../../../../model/enums/appConfigurationKey'
import { ObjectionTableModel } from '../../../../pages/invoicesPage/invoicesPage.Model'
import { CompanyStoreModel, getAppConfigurationValue } from '../../../../redux'
import AxiosInstance from '../../../http-config'
import { EnumGrupoDocumento } from '../../../oData'
import { AtlasDocumentApiResponse } from './model/atlasDocument'
import { DocumentModel, DocumentPatchRequest, DocumentPostRequest } from './model/document'

export const apiFetchDocumentsByIdList = async (
  documentIdList: number[]
): Promise<DocumentModel[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  const queryString = documentIdList.map(id => `documentIdList=${id}`).join('&')
  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/Documents/GetDocumentsByID?${queryString}`
  ).then(response => response.data)
}

export const apiFetchDocumentsByProcedureId = async (
  procedureId: number
): Promise<DocumentModel[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/Documents/GetDocumentsByProcedureId(${procedureId})`
  ).then(response => response.data)
}

export const apiFetchDocumentsByContractId = async (
  contractId: number
): Promise<DocumentModel[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/Documents/GetDocumentsByContractId(${contractId})`
  ).then(response => response.data)
}

export const apiFetchDocumentsByTradeId = async (tradeId: number): Promise<DocumentModel[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/Documents/GetDocumentsByTradeId(${tradeId})`
  ).then(response => response.data)
}

export const apiFecthDocumentTypeByDocumentGorup = async (
  documentGroupId: number
): Promise<SelectOption[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/Documents/GetDocumentsTypesByDocumentGroup(${documentGroupId})`
  ).then(response => response.data)
}

export const apiFecthDocumentTypeByDocumentGroupList = async (
  documentGroupList: number[]
): Promise<SelectOption[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  const queryString = documentGroupList.map(groupId => `documentGroupList=${groupId}`).join('&')

  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/Documents/GetDocumentsTypesByDocumentGroupList?${queryString}`
  ).then(response => response.data)
}

export const apiFetchDocumentsByCustomFilter = async (
  types: EnumGrupoDocumento[],
  dateRange: DatePeriod,
  company: CompanyStoreModel | undefined
): Promise<DocumentModel[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  let recordsLimit: number | null = null

  switch (dateRange.dateFilter) {
    case DateFilters.RECORDS_LIM_500:
      recordsLimit = 500
      break
    default:
      recordsLimit = null
  }
  const parseStartDate = formatDateOnlyString(dateRange.start)
  const parseEndDate = formatDateOnlyString(dateRange.end)
  const params: string[] = []

  if (company?.id) {
    params.push(`company=${company.id}`)
  }
  if (dateRange.customFilter?.documentType) {
    params.push(`documentType=${dateRange.customFilter.documentType}`)
  }
  if (recordsLimit) {
    params.push(`recordsLimit=${recordsLimit}`)
  } else {
    if (parseStartDate) params.push(`startDate=${parseStartDate}`)
    if (parseEndDate) params.push(`endDate=${parseEndDate}`)
  }
  if (types?.length) {
    params.push(types.map(group => `documentGroupList=${group}`).join('&'))
  }
  const queryString = params.length ? `?${params.join('&')}` : ''

  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/Documents/GetDocumentsByCustomFilter${queryString}`
  ).then(response => response.data)
}

export const apiPostDocument = async (
  request: DocumentPostRequest
): Promise<DocumentModel | undefined> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.post(`${apiSuffixUrl}/api/Documents/CreateDocument`, request).then(
    response => response.data
  )
}

export const apiPatchDocument = async (
  request: DocumentPatchRequest
): Promise<DocumentModel | undefined> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.patch(
    `${apiSuffixUrl}/api/Documents/UpdateDocument(${request.id!})`,
    request
  ).then(response => response.data)
}

export const apiDeleteDocument = async (documentId: number) => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.delete(
    `${apiSuffixUrl}/api/Documents/DeleteDocument(${documentId})`
  ).then(response => response.data)
}

export const apiFetchDownloadDocument = async (elementId: number): Promise<any> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

  return await AxiosInstance.get(`${apiSuffixUrl}/api/FilesLoading/downloadFile/${elementId}`, {
    responseType: 'blob'
  })
}

export const apiGetDocumentsByType = async (typeID: number[]): Promise<DocumentModel[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  const queryString = typeID.map(id => `typeID=${id}`).join('&')

  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/Documents/GetDocumentsByType?${queryString}`
  ).then(response => {
    if (response.data) {
      return response.data
    } else {
      return []
    }
  })
}

export const apiGetDocumentsByDocumentGroupList = async (
  documentGroupList: number[]
): Promise<AtlasDocumentApiResponse[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  const queryString = documentGroupList.map(id => `documentGroupList=${id}`).join('&')

  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/Documents/GetDocumentsByDocumentGroupList?${queryString}`
  ).then(response => {
    if (response.data) {
      return response.data
    } else {
      return []
    }
  })
}

export const apiGetDocumentsByProfileId = async (profileId: number): Promise<DocumentModel[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/Documents/GetDocumentsByProfileId(${profileId})`
  ).then(response => {
    if (response.data) {
      return response.data
    } else {
      return []
    }
  })
}

export const apiGetDocumentsByLeadId = async (leadId: number): Promise<DocumentModel[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/Documents/GetDocumentsByLeadId(${leadId})`
  ).then(response => {
    if (response.data) {
      return response.data
    } else {
      return []
    }
  })
}

// ------------ Document Objections ------------ //

export const apiFetchDocumentObjectionsDescriptions = async (): Promise<ObjectionTableModel[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/Documents/FetchDocumentObjectionsDescriptions`
  ).then(response => response.data)
}

export const apiFetchObjectionsByDocumentId = async (
  documentID: number
): Promise<ObjectionTableModel[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.get(`${apiSuffixUrl}/api/Documents/${documentID}/Objections`).then(
    response => response.data
  )
}
